import {Form, TreeSelect} from 'antd';
import {FormInstance} from 'antd/lib/form';
import _ from 'lodash';
import React, {ReactText, useEffect, useState} from 'react';
import {findCode, findKey} from '../../reducers/util';
import {ICDNode} from './types/ICDNode';
import {LabelAndValue} from './types/LabelAndValue';
import {extractKeys} from './TreeSelectUtil';
import {findMatchingNodes} from "./TreeMatcher";
import {showWarning, validateFieldsWithErrors} from "./SelectUtil";
import TreeSelectWrapper from './TreeSelectWrapper';
import { getChildProceduresBlock, searchForProcedures } from './ProceduresSelectorApi';

const { TreeNode } = TreeSelect;

interface Props {
  form: FormInstance
  fieldId?: string
  fieldName?: string // label in UI
  dependencies?: string[]
  customValidator?(rule: any, value: any): Promise<void>
  model?: LabelAndValue[]
  procedureTree: ICDNode[]
  useIpdn?: boolean
}

const ProceduresSelector = ({
                              form,
                              fieldId = 'procedures',
                              fieldName = 'Procedure',
                              dependencies,
                              customValidator,
                              model,
                              procedureTree,
                              useIpdn = false
                            }: Props) => {

  const [expandedKeys, setExpandedKeys] = useState(new Array<ReactText>())

  const [treeData, setTreeData] = useState(procedureTree)

  const [treeNodes, setTreeNodes] = useState(new Array<JSX.Element>())

  const [searchString, setSearchString] = useState("");

  const [paste, setPaste] = useState(false)

  const toTreeNode = (data: ICDNode[]) => {
    return data.map((item) => {
        const title = item.title
        const value = item.code
        const key = item.code
        const code = item.code
        
        return (<TreeNode 
            title={title} 
            value={value} 
            key={key} 
            code={code} 
            xparent={item.parent}
            xkey={item.key}
            isLeaf={!(item.folder)}
            children={item.children ? toTreeNode(item.children) : undefined}
        />) as JSX.Element
    });
  };

  useEffect(() => {
    setTreeNodes(toTreeNode(treeData))
}, [treeData]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = _.debounce(((value: string) => {
    setSearchString(value);
    if (value.length >= 3) {
      searchForProcedures(value, useIpdn)
        .then(data => {
          if (paste) {
            const currentValue = form.getFieldValue(fieldId);
            let newValue: LabelAndValue[] = []
            if (currentValue) {
              newValue = JSON.parse(JSON.stringify(currentValue))
            }
            if (newValue) {
              const matchingNodes = findMatchingNodes(value, data);
              newValue.push(...matchingNodes.matched)
              form.setFieldsValue({[fieldId]: _.uniqBy(newValue, 'value')})
              validateFieldsWithErrors(form)
              if (matchingNodes.unmatched && matchingNodes.unmatched.length > 0) {
                showWarning(fieldName, matchingNodes.unmatched.join(", "))
              }
            }
            setPaste(false)
          }

          setTreeNodes(toTreeNode(data))
          setExpandedKeys(extractKeys(data))
        
      })
    } else {
      setExpandedKeys([])
      setTreeData(JSON.parse(JSON.stringify(procedureTree)));
    }
  }), 500);

  useEffect(() => {
    return () => onSearch.cancel();
  }, [onSearch])

  const onLoadKey = (key : string) : Promise<unknown> => {
      return getChildProceduresBlock(key, useIpdn)
        .then(data => {
            const newTreeData = JSON.parse(JSON.stringify(treeData));
            const result = findKey(newTreeData, key)
            if (result) {
                result.children = data
                result.isLoaded = true
                setTreeData(newTreeData)
            }
        })
  }


  const rules = () => {
    return _.isNil(customValidator) ? [] : [{ validator: customValidator }]
  }

  return <Form.Item
      name={fieldId}
      label={fieldName}
      initialValue={_.isNil(model) ? undefined : model}
      rules={rules()}
      dependencies={dependencies}
  >
    <TreeSelectWrapper
      onDropdownVisibleChange={() => { setExpandedKeys([]) }}
      treeCheckStrictly={true}
      treeCheckable={true}
      onSearch={onSearch}
      // @ts-ignore
      allowClear={true}
      multiple={true}
      showSearch={true}
      // style={{ width: '100%' }}
      placeholder={"Select " + fieldName}
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      // dropdownStyle={{ maxHeight: 200, overflow: 'auto' }}
      filterTreeNode={false}
      autoClearSearchValue={false}
      treeExpandedKeys={expandedKeys}
      onTreeExpand={(expandedKeys: ReactText[]) => {
        if (searchString.length < 3) {
          const loadKeys = expandedKeys
          .map(key => findCode(treeData, `${key}`))
          .filter(treeNode => treeNode !== undefined && !(treeNode.isLoaded))
          .map(treeNode => onLoadKey(treeNode!.key))
          Promise.all(loadKeys)
            .then(() => setExpandedKeys(expandedKeys));
        } else {
          setExpandedKeys(expandedKeys);
        }
      }}
      onPaste={(e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        setPaste(true)
        return true;
      }}
      data-testid='procedure-tree'
      >
        {treeNodes}
      </TreeSelectWrapper>
  </Form.Item>
}

export default ProceduresSelector